import React, {
	useState,
	useRef,
	forwardRef,
	useImperativeHandle
} from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import { Checkbox } from "@material-ui/core";

import * as api from "services/api";

import { contractTypeToNumeric } from "types/contractSolicitation";

import { validateEmail } from "helpers/email";
import { validateUrl } from "helpers/url";

import { DatePickerInput } from "components/DatePicker";
import { SearchInput } from "components/SearchInput";
import { SelectInput } from "components/SelectInput";
import { TextInput } from "components/TextInput";

import { CheckboxContainer, ModalBody, RowContainer, Title } from "./styles";

export const ContractFormStep = forwardRef(
	(
		{
			step,
			contractSolicitation,
			lastDocumentName,
			lastDocumentURL,
			solicitationId,
			onValidationChange
		},
		ref
	) => {
		const [selectedContract, setSelectedContract] = useState(null);
		const [contractSearchLoading, setContractSearchLoading] = useState(false);
		const [contracts, setContracts] = useState([]);
		const [isSelfRenewable, setIsSelfRenewable] = useState(false);
		const [validationErrors, setValidationErrors] = useState({});

		const { token } = useSelector(s => s.login);

		const formRef = useRef(null);

		function validateForm(formData) {
			const errors = {};

			if (!formData.get("contractName")?.trim()) {
				errors.contractName = "Nome do contrato é obrigatório";
			}

			if (!formData.get("contractType")) {
				errors.contractType = "Tipo de contrato é obrigatório";
			}

			if (!formData.get("startDate")) {
				errors.startDate = "Data de início é obrigatória";
			}

			if (!formData.get("lifespan") || Number(formData.get("lifespan")) <= 0) {
				errors.lifespan = "Vigência é obrigatória e deve ser maior que 0";
			}

			if (!formData.get("contractCode")?.trim()) {
				errors.contractCode = "Código do contrato é obrigatório";
			}

			if (!formData.get("hirerContractURL")?.trim()) {
				errors.hirerContractURL =
					"Link de assinatura do contratante é obrigatório";
			}

			if (!formData.get("hirerContractSignerEmail")?.trim()) {
				errors.hirerContractSignerEmail = "E-mail do contratante é obrigatório";
			}

			if (
				formData.get("contractHirerStatus") === null ||
				formData.get("contractHirerStatus") === ""
			) {
				errors.contractHirerStatus =
					"Status do contrato (contratante) é obrigatório";
			}

			if (!formData.get("hiredContractURL")?.trim()) {
				errors.hiredContractURL =
					"Link de assinatura do prestador é obrigatório";
			}

			if (!formData.get("hiredContractSignerEmail")?.trim()) {
				errors.hiredContractSignerEmail = "E-mail do prestador é obrigatório";
			}

			if (
				formData.get("contractHiredStatus") === null ||
				formData.get("contractHiredStatus") === ""
			) {
				errors.contractHiredStatus =
					"Status do contrato (prestador) é obrigatório";
			}

			if (step === "referenceContract" && !selectedContract) {
				errors.mainContract = "Contrato principal é obrigatório";
			}

			if (
				formData.get("hirerContractSignerEmail") &&
				!validateEmail(formData.get("hirerContractSignerEmail"))
			) {
				errors.hirerContractSignerEmail =
					"E-mail do contratante deve ter um formato válido";
			}

			if (
				formData.get("hiredContractSignerEmail") &&
				!validateEmail(formData.get("hiredContractSignerEmail"))
			) {
				errors.hiredContractSignerEmail =
					"E-mail do prestador deve ter um formato válido";
			}

			if (
				formData.get("hirerContractURL") &&
				!validateUrl(formData.get("hirerContractURL"))
			) {
				errors.hirerContractURL =
					"Link de assinatura do contratante deve ser uma URL válida";
			}

			if (
				formData.get("hiredContractURL") &&
				!validateUrl(formData.get("hiredContractURL"))
			) {
				errors.hiredContractURL =
					"Link de assinatura do prestador deve ser uma URL válida";
			}

			return errors;
		}

		function isFormValid() {
			if (!formRef.current) return false;

			const formData = new FormData(formRef.current);
			const errors = validateForm(formData);

			return Object.keys(errors).length === 0;
		}

		function collectFormData() {
			if (!formRef.current) return null;

			const formData = new FormData(formRef.current);

			const errors = validateForm(formData);
			setValidationErrors(errors);

			if (Object.keys(errors).length > 0) {
				return null;
			}

			const data = {};

			data.mei = contractSolicitation?.hirer?._id || "";
			data.hired = contractSolicitation?.hired?._id || "";

			data.name = formData.get("contractName") || "";
			const contractType = formData.get("contractType") || "";
			data.type =
				contractTypeToNumeric[contractType] !== undefined
					? contractTypeToNumeric[contractType]
					: 0;

			const startDate = formData.get("startDate");
			if (startDate) {
				const [day, month, year] = startDate.split("/");
				data.startDate = new Date(year, month - 1, day);
			}

			const lifespan = formData.get("lifespan");
			if (lifespan) data.lifespan = Number(lifespan);

			data.selfRenewable = isSelfRenewable;

			const contractCode = formData.get("contractCode");
			if (contractCode) data.externalId = contractCode;

			const hirerContractURL = formData.get("hirerContractURL");
			if (hirerContractURL) data.hirerContractURL = hirerContractURL;

			const hirerContractSignerEmail = formData.get("hirerContractSignerEmail");
			if (hirerContractSignerEmail)
				data.hirerContractSignerEmail = hirerContractSignerEmail;

			const contractHirerStatus = formData.get("contractHirerStatus");
			if (contractHirerStatus) data.hirerStatus = Number(contractHirerStatus);

			const hiredContractURL = formData.get("hiredContractURL");
			if (hiredContractURL) data.hiredContractURL = hiredContractURL;

			const hiredContractSignerEmail = formData.get("hiredContractSignerEmail");
			if (hiredContractSignerEmail)
				data.hiredContractSignerEmail = hiredContractSignerEmail;

			const contractHiredStatus = formData.get("contractHiredStatus");
			if (contractHiredStatus) data.hiredStatus = Number(contractHiredStatus);

			if (lastDocumentURL) data.file = lastDocumentURL;

			data.solicitationId = solicitationId || "";

			data.isMainContract = step === "mainContract";

			if (step === "referenceContract" && selectedContract) {
				data.contractId = selectedContract.value;
			}

			return data;
		}

		useImperativeHandle(ref, () => ({
			collectFormData,
			isFormValid
		}));

		let findContractList = null;

		function handleSelectContract(contract) {
			setSelectedContract(contract);
			if (validationErrors.mainContract) {
				setValidationErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors.mainContract;
					return newErrors;
				});
			}
		}

		function handleFieldChange(fieldName) {
			if (validationErrors[fieldName]) {
				setValidationErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors[fieldName];
					return newErrors;
				});
			}
		}

		function validateSingleField(fieldName, value) {
			const normalizedValue =
				value === null || value === undefined ? "" : String(value);

			let fieldError = null;

			switch (fieldName) {
				case "contractName":
					if (!normalizedValue.trim()) {
						fieldError = "Nome do contrato é obrigatório";
					}
					break;

				case "contractType":
					if (!normalizedValue) {
						fieldError = "Tipo de contrato é obrigatório";
					}
					break;

				case "startDate":
					if (!normalizedValue) {
						fieldError = "Data de início é obrigatória";
					}
					break;

				case "lifespan":
					if (!normalizedValue || Number(normalizedValue) <= 0) {
						fieldError = "Vigência é obrigatória e deve ser maior que 0";
					} else if (Number(normalizedValue) > 999) {
						fieldError = "Vigência deve ser menor que 999";
					}
					break;

				case "contractCode":
					if (!normalizedValue.trim()) {
						fieldError = "Código do contrato é obrigatório";
					}
					break;

				case "hirerContractURL":
					if (!normalizedValue.trim()) {
						fieldError = "Link de assinatura do contratante é obrigatório";
					} else if (!validateUrl(normalizedValue)) {
						fieldError =
							"Link de assinatura do contratante deve ser uma URL válida";
					}
					break;

				case "hirerContractSignerEmail":
					if (!normalizedValue.trim()) {
						fieldError = "E-mail do contratante é obrigatório";
					} else if (!validateEmail(normalizedValue)) {
						fieldError = "E-mail do contratante deve ter um formato válido";
					}
					break;

				case "contractHirerStatus":
					if (value === null || value === "" || value === undefined) {
						fieldError = "Status do contrato (contratante) é obrigatório";
					}
					break;

				case "hiredContractURL":
					if (!normalizedValue.trim()) {
						fieldError = "Link de assinatura do prestador é obrigatório";
					} else if (!validateUrl(normalizedValue)) {
						fieldError =
							"Link de assinatura do prestador deve ser uma URL válida";
					}
					break;

				case "hiredContractSignerEmail":
					if (!normalizedValue.trim()) {
						fieldError = "E-mail do prestador é obrigatório";
					} else if (!validateEmail(normalizedValue)) {
						fieldError = "E-mail do prestador deve ter um formato válido";
					}
					break;

				case "contractHiredStatus":
					if (value === null || value === "" || value === undefined) {
						fieldError = "Status do contrato (prestador) é obrigatório";
					}
					break;

				default:
					break;
			}

			setValidationErrors(prev => {
				const newErrors = { ...prev };
				if (fieldError) {
					newErrors[fieldName] = fieldError;
				} else {
					delete newErrors[fieldName];
				}
				return newErrors;
			});

			if (onValidationChange) {
				onValidationChange();
			}
		}

		function handleSearchContracts(templateName) {
			if (
				!templateName ||
				templateName.trim() === "" ||
				!contractSolicitation?.hirer?._id ||
				!contractSolicitation?.hired?._id
			) {
				setContracts([]);
				setContractSearchLoading(false);
				return;
			}

			if (findContractList) {
				findContractList.cancel();
			}

			setContractSearchLoading(true);

			const filterStr = `&filters[mei]=${contractSolicitation.hirer._id}&filters[hired]=${contractSolicitation.hired._id}&filters[template.name]=${templateName}`;

			findContractList = _.debounce(
				() =>
					api
						.sendGet(
							`/contracts?skip=0&take=10&sort=createdAt&desc=true${filterStr}`,
							{
								Authorization: "Bearer " + token
							}
						)
						.then(response => response.json())
						.then(result => {
							if (result.isValid) {
								const contractOptions = result.model.map(contract => ({
									value: contract._id,
									label: `${contract.template.name} - ID: ${contract._id}`
								}));
								setContracts(contractOptions);
							}
							setContractSearchLoading(false);
						})
						.catch(error => {
							console.error("Erro ao buscar contratos:", error);
							setContractSearchLoading(false);
						}),
				500
			);

			findContractList();
		}

		return (
			<ModalBody ref={formRef}>
				<Title>INFORMAÇÕES DO CONTRATO</Title>

				<TextInput
					id="hirer"
					name="hirer"
					label="Contratante"
					value={contractSolicitation?.hirer?.businessName}
					disabled
				/>

				<TextInput
					id="hired"
					name="hired"
					label="Prestador"
					value={contractSolicitation?.hired?.businessName}
					disabled
				/>

				{step === "referenceContract" && (
					<SearchInput
						id="mainContract"
						name="mainContract"
						placeholder="Buscar contrato principal"
						label="Buscar contrato principal"
						onChange={handleSelectContract}
						onSearch={handleSearchContracts}
						options={contracts}
						loading={contractSearchLoading}
						error={validationErrors.mainContract}
						required
					/>
				)}

				<TextInput
					id="contractName"
					name="contractName"
					label="Nome do contrato"
					placeholder="Nome do contrato"
					error={validationErrors.contractName}
					required
					onChange={() => handleFieldChange("contractName")}
					onBlur={e => validateSingleField("contractName", e.target.value)}
				/>

				<SelectInput
					id="contractType"
					name="contractType"
					label="Tipo de contrato"
					defaultValue={contractSolicitation?.contractType}
					error={validationErrors.contractType}
					required
					onChange={value => {
						handleFieldChange("contractType");
						validateSingleField("contractType", value);
					}}
					options={[
						{ label: "NDA", value: "nda" },
						{ label: "Confidencialidade", value: "confidentiality" },
						{ label: "Comodato", value: "loan" },
						{ label: "Prestação de Serviço", value: "serviceProvision" },
						{ label: "Aditivo contratual", value: "addendum" },
						{ label: "Distrato", value: "termination" },
						{ label: "Outros", value: "other" }
					]}
				/>

				<RowContainer>
					<DatePickerInput
						id="startDate"
						name="startDate"
						placeholder="Ex: 01/01/1970"
						label="Data de início"
						value={contractSolicitation?.startDate}
						error={validationErrors.startDate}
						required
						onChange={() => handleFieldChange("startDate")}
						onBlur={value => validateSingleField("startDate", value)}
					/>
					<TextInput
						id="lifespan"
						name="lifespan"
						placeholder="12"
						type="number"
						label="Vigência (em meses)"
						defaultValue={Number(contractSolicitation?.lifespan)}
						error={validationErrors.lifespan}
						min={0}
						max={999}
						required
						onChange={() => handleFieldChange("lifespan")}
						onBlur={e => validateSingleField("lifespan", e.target.value)}
					/>
				</RowContainer>

				<CheckboxContainer>
					<Checkbox
						color="primary"
						id="selfRenewable"
						name="selfRenewable"
						onChange={() => setIsSelfRenewable(prevState => !prevState)}
					/>
					<span>Contrato autorrenovável</span>
				</CheckboxContainer>

				<TextInput
					id="contractCode"
					name="contractCode"
					label="Código do contrato"
					placeholder="81723123-118371824-123781-182371238"
					error={validationErrors.contractCode}
					required
					onChange={() => handleFieldChange("contractCode")}
					onBlur={e => validateSingleField("contractCode", e.target.value)}
				/>

				<TextInput
					id="hirerContractURL"
					name="hirerContractURL"
					label="Link de assinatura do contratante"
					placeholder="http://zapsign.doc/aYGASbdajsu331"
					error={validationErrors.hirerContractURL}
					required
					onChange={() => handleFieldChange("hirerContractURL")}
					onBlur={e => validateSingleField("hirerContractURL", e.target.value)}
				/>

				<RowContainer>
					<TextInput
						id="hirerContractSignerEmail"
						name="hirerContractSignerEmail"
						label="E-mail do contratante"
						placeholder="contratante@zapsign.com"
						error={validationErrors.hirerContractSignerEmail}
						required
						onChange={() => handleFieldChange("hirerContractSignerEmail")}
						onBlur={e =>
							validateSingleField("hirerContractSignerEmail", e.target.value)
						}
					/>
					<SelectInput
						id="contractHirerStatus"
						name="contractHirerStatus"
						label="Status contrato"
						error={validationErrors.contractHirerStatus}
						required
						onChange={value => {
							handleFieldChange("contractHirerStatus");
							validateSingleField("contractHirerStatus", value);
						}}
						options={[
							{ label: "Pendente", value: 0 },
							{ label: "Assinado", value: 1 }
						]}
					/>
				</RowContainer>

				<TextInput
					id="hiredContractURL"
					name="hiredContractURL"
					label="Link de assinatura do prestador"
					placeholder="http://zapsign.doc/aYGASbdajsu331"
					error={validationErrors.hiredContractURL}
					required
					onChange={() => handleFieldChange("hiredContractURL")}
					onBlur={e => validateSingleField("hiredContractURL", e.target.value)}
				/>

				<RowContainer>
					<TextInput
						id="hiredContractSignerEmail"
						name="hiredContractSignerEmail"
						label="E-mail do prestador"
						placeholder="prestador@zapsign.com"
						error={validationErrors.hiredContractSignerEmail}
						required
						onChange={() => handleFieldChange("hiredContractSignerEmail")}
						onBlur={e =>
							validateSingleField("hiredContractSignerEmail", e.target.value)
						}
					/>
					<SelectInput
						id="contractHiredStatus"
						name="contractHiredStatus"
						label="Status contrato"
						error={validationErrors.contractHiredStatus}
						required
						onChange={value => {
							handleFieldChange("contractHiredStatus");
							validateSingleField("contractHiredStatus", value);
						}}
						options={[
							{ label: "Pendente", value: 0 },
							{ label: "Assinado", value: 1 }
						]}
					/>
				</RowContainer>

				<TextInput
					id="baseContractFile"
					name="baseContractFile"
					label="Nome do documento PDF"
					placeholder="Arquivo base do contrato"
					value={lastDocumentName}
					disabled
				/>
			</ModalBody>
		);
	}
);
